/**
 * ProShip Direct — zone mapping for the merchant's own courier.
 *
 * TWO CHOICES, and the screen is the choice.
 *  - Use ProShip's: nothing is copied. When a courier has no zone folder of its own the platform reads
 *    its parent carrier's mapping, so choosing this only removes any folder the merchant uploaded.
 *  - Upload their own: one matrix — warehouses down column A, customer pincodes across row 1, a zone
 *    in each cell. The server validates the whole file before writing anything, then writes it to S3
 *    as a background job whose progress shows here.
 *
 * ONE FILE FOR BOTH DIRECTIONS. Rows are always the merchant's warehouses and columns always the
 * customers; the server works out which is the pickup. That is why A1 names the sides rather than
 * saying pickup/drop, which is only true for forward.
 *
 * HONEST ABOUT "USE PROSHIP'S". `defaultActive` says whether the parent's mapping is actually reaching
 * this courier today — it can be the merchant's choice and still not be in effect, and the screen
 * says so instead of implying they have zones.
 *
 * REPLACED A FIXTURE PAGE that parsed lanes into browser storage.
 */
import React, { useState } from 'react';
import {
  Box, Grid, Typography, Button, Alert, Chip, Divider, CircularProgress, LinearProgress,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import withLayout from '../../../hocs/Layout';
import SetupPageShell from '../components/SetupPageShell';
import FileDropZone from '../components/FileDropZone';
import Loader from '../../../common/Loader';
import { SETUP_DIMENSIONS } from '../data/defaults';
import { useCourierZones } from '../data/hooks';
import { sectionCardSx, sectionTitleSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { tHeadSx, tCellSx, statCardSx, statLabelSx, statValueSx } from '../components/stats';
import { SUBTLE, NAVY, SUCCESS, BORDER, WARNING } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { formatDate } from '../../../helpers/UtilityHelper';
import { downloadCsv } from '../data/serviceabilityUpload';
import { MONO } from '../components/directCellStyles';

const dimension = SETUP_DIMENSIONS.find((d) => d.key === 'zones');

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.6 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };

/** The zone numbers a cell may hold — the same ones the merchant's rate card will price. */
const PROZO_ZONES = [
  { zone: 'Zone 1', scope: 'Within city', example: 'Delhi → Delhi' },
  { zone: 'Zone 2', scope: 'Within state', example: 'Mumbai → Pune' },
  { zone: 'Zone 3', scope: 'Metro to metro', example: 'Delhi → Bengaluru' },
  { zone: 'Zone 4', scope: 'Rest of India', example: 'Delhi → Indore' },
  { zone: 'Zone 5', scope: 'North-east, J&K, islands', example: 'Delhi → Guwahati' },
];

/**
 * The downloadable sample. A1 is informational — the server reads by position — but it is the one
 * place a merchant learns which way round the file goes, so it names both axes.
 */
const TEMPLATE = [
  'Warehouse (rows) / Customer (columns),246170,246173,246175,246176,246177,246185',
  '744101,5,5,5,5,5,5',
  '744102,5,5,5,5,5,5',
  '522409,4,4,4,4,4,4',
  '',
].join('\n');

const nfmt = (n) => Number(n || 0).toLocaleString('en-IN');

function Coverage({ account }) {
  const { status, loading, error, running, upload, switchToDefault } = useCourierZones(account?.accountCode);
  const [staged, setStaged] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  if (loading) return <Loader />;

  const reverse = status?.direction === 'RVP';
  const ownFiles = status?.ownFileCount || 0;
  const onDefault = !running && ownFiles === 0;

  const run = async (fn) => {
    setBusy(true);
    setProblem(null);
    try {
      await fn();
      setStaged(null);
    } catch (e) {
      setProblem(e.message);
    } finally {
      setBusy(false);
    }
  };

  const pct = status?.jobTotal ? Math.round(((status.jobDone || 0) + (status.jobFailed || 0)) * 100 / status.jobTotal) : 0;

  return (
    <>
      {error && <Alert severity='warning' sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>{error}</Alert>}

      {/* ── What this courier runs on now ── */}
      <Box sx={{ ...sectionCardSx, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap', mb: 1 }}>
          <Typography sx={sectionTitleSx}>Zones in use</Typography>
          <Chip
            size='small'
            label={ownFiles > 0 ? 'Yours' : 'ProShip’s'}
            sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], fontWeight: WEIGHT.semibold, height: 20, bgcolor: `${SUCCESS}14`, color: SUCCESS }}
          />
        </Box>

        {ownFiles > 0 ? (
          <Grid container spacing={1}>
            <Grid item xs={6} md={3}><Box sx={statCardSx}><Typography sx={statLabelSx}>Warehouses</Typography><Typography sx={statValueSx}>{nfmt(status.warehouseCount)}</Typography></Box></Grid>
            <Grid item xs={6} md={3}><Box sx={statCardSx}><Typography sx={statLabelSx}>Customer pincodes</Typography><Typography sx={statValueSx}>{nfmt(status.customerCount)}</Typography></Box></Grid>
            <Grid item xs={6} md={3}><Box sx={statCardSx}><Typography sx={statLabelSx}>Lanes with a zone</Typography><Typography sx={statValueSx}>{nfmt(status.laneCount)}</Typography></Box></Grid>
            <Grid item xs={6} md={3}><Box sx={statCardSx}><Typography sx={statLabelSx}>Files live</Typography><Typography sx={statValueSx}>{nfmt(ownFiles)}</Typography></Box></Grid>
          </Grid>
        ) : status?.defaultActive ? (
          <Typography sx={bodySx}>
            This courier uses ProShip’s zone mapping for {account.carrierName}. Nothing was copied, so
            whenever ProShip updates that mapping you get it automatically.
          </Typography>
        ) : (
          /*
            Chosen, not yet in effect. Either the platform's parent-mapping fallback is off, or this
            carrier has no ProShip mapping ready. Saying so beats letting the merchant believe their
            shipments will be zoned.
          */
          <Alert severity='warning' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
            ProShip’s zone mapping is not reaching this courier yet, so its shipments cannot be zoned or
            priced. Upload your own below, or contact your account manager.
          </Alert>
        )}

        {status?.updatedAt && (
          <Typography sx={{ ...smallSx, mt: 1 }}>
            Last changed {formatDate(status.updatedAt, 'dd MMM yyyy, hh:mm a')}
            {status.updatedBy ? ` by ${status.updatedBy}` : ''}
            {status.fileName ? ` · ${status.fileName}` : ''}
          </Typography>
        )}

        {/* ── A job in flight, or the last one's outcome ── */}
        {status?.jobId && status.jobProgress && (
          <Box sx={{ mt: 1.25 }}>
            {running ? (
              <>
                <Typography sx={{ ...smallSx, mb: 0.5 }}>
                  {status.jobOp === 'RESET' ? 'Removing your zones' : 'Writing your zones'} — {nfmt((status.jobDone || 0) + (status.jobFailed || 0))} of {nfmt(status.jobTotal)} files ({pct}%)
                </Typography>
                <LinearProgress variant='determinate' value={pct} />
              </>
            ) : status.jobProgress === 'FAILED' || status.jobFailed > 0 ? (
              <Alert severity='error' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                {nfmt(status.jobFailed)} of {nfmt(status.jobTotal)} files could not be written. Upload the file
                again — every file is rewritten, so a retry fixes it.
              </Alert>
            ) : null}
          </Box>
        )}
      </Box>

      {/* ── Use ProShip's ── */}
      {!onDefault && !running && (
        <Box sx={{ ...sectionCardSx, mb: 1.5 }}>
          <Typography sx={{ ...sectionTitleSx, mb: 0.5 }}>Use ProShip’s zones instead</Typography>
          <Typography sx={{ ...bodySx, mb: 1.25 }}>
            Removes your zone mapping from this courier so it uses ProShip’s mapping for {account.carrierName}.
            Nothing is copied — it stays in step with ProShip’s from then on.
          </Typography>
          <Button
            size='small'
            sx={secondaryBtnSx}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={13} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
            onClick={() => run(switchToDefault)}
          >
            Use ProShip’s zones
          </Button>
        </Box>
      )}

      {/* ── Upload your own ── */}
      <Box sx={{ ...sectionCardSx, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap', mb: 0.75 }}>
          <Typography sx={sectionTitleSx}>Upload your own</Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size='small'
            sx={secondaryBtnSx}
            startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => downloadCsv('zone-mapping-template.csv', TEMPLATE)}
          >
            Download sample
          </Button>
        </Box>
        <Typography sx={{ ...bodySx, mb: 1.5 }}>
          Your warehouse pincodes down column A, the customer pincodes you serve across row 1, and the
          zone for each pair in the cell where they meet. Leave a cell blank if you don’t serve that
          pair. The same layout works for forward and returns — for this {reverse ? 'returns' : 'forward'} courier
          we read it {reverse ? 'from each customer back to your warehouses' : 'from each warehouse out to your customers'}.
          Applies to <Box component='span' sx={{ fontFamily: MONO }}>{account.accountCode}</Box> only, and replaces
          any zones it had.
        </Typography>

        {staged ? (
          <Box sx={{ border: `1px solid ${SUCCESS}`, borderRadius: RADIUS.md, p: 1.25, mb: 1.5 }}>
            <Typography sx={strongSx}>{staged.name}</Typography>
            <Typography
              onClick={() => setStaged(null)}
              sx={{ ...smallSx, color: WARNING, cursor: 'pointer', textDecoration: 'underline', mt: 0.4 }}
            >
              Remove
            </Typography>
          </Box>
        ) : (
          <FileDropZone
            onFile={(f) => { setProblem(null); setStaged(f); }}
            disabled={running}
            hint={running ? 'Wait for the current job to finish.' : 'One CSV, up to 50 MB.'}
          />
        )}

        {/* The server lists every problem with its cell reference — keep the line breaks. */}
        {problem && (
          <Alert severity='error' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'], whiteSpace: 'pre-line' }}>
            {problem}
          </Alert>
        )}

        <Divider sx={{ my: 1.5, borderColor: BORDER }} />
        <Button
          size='small'
          variant='contained'
          sx={primaryBtnSx}
          disabled={!staged || busy || running}
          startIcon={busy ? <CircularProgress size={13} /> : null}
          onClick={() => run(() => upload(staged))}
        >
          {busy ? 'Checking the file…' : 'Upload zones'}
        </Button>
      </Box>

      {/* ── What the zone numbers mean ── */}
      <Box sx={sectionCardSx}>
        <Typography sx={{ ...sectionTitleSx, mb: 0.75 }}>Zone numbers</Typography>
        <Typography sx={{ ...smallSx, mb: 1 }}>
          Use ProShip’s zone numbers in the cells. Your rate card prices these same zones, so a zone you use
          here needs a price there.
        </Typography>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={tHeadSx}>Zone</TableCell>
              <TableCell sx={tHeadSx}>Covers</TableCell>
              <TableCell sx={tHeadSx}>Example</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PROZO_ZONES.map((z) => (
              <TableRow key={z.zone}>
                <TableCell sx={tCellSx}>{z.zone}</TableCell>
                <TableCell sx={tCellSx}>{z.scope}</TableCell>
                <TableCell sx={tCellSx}>{z.example}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
}

export function Zones({ embedded = false }) {
  return (
    <SetupPageShell dimension={dimension} embedded={embedded} serverManaged>
      {({ account }) => <Coverage account={account} />}
    </SetupPageShell>
  );
}

export default withLayout(Zones);
